import R from 'ramda';
import moment from 'moment';
import { SYSTEM_ROLES } from '../../server/constants';

// paths([['a', 'b', 'c'], ['x']], obj); //=> [1, 2]
export const paths = R.curry((paths, obj) => R.ap([R.path(R.__, obj)], paths))
export const toTitle = R.compose(
  R.join(''),
  R.over(R.lensIndex(0), R.toUpper)
)

// export const escapeRE = R.replace(new RegExp(/([.*+?^=!:$(){}|[\]\/\\])/g), '\\$1')
export const escapeRE = R.compose(
  R.replace(new RegExp(/([.*+?^=!:$(){}|[\]/\\])/g), '\\$1'),
  R.trim,
  R.defaultTo('')
)
export const mapIndexed = R.addIndex(R.map)
export const notNil = R.complement(R.isNil)
export const notEmpty = R.complement(R.isEmpty)
export const notNilOrEmpty = R.both(notNil, notEmpty)
export const isNilOrEmpty = R.either(R.isNil, R.isEmpty)
export const trimIfNotNil = R.ifElse(
  R.both(R.is(String), notNil),
  R.trim,
  R.identity
)
export const toIntOr20 = R.tryCatch(parseInt, R.always(20))
export const toIntOr1 = R.tryCatch(parseInt, R.always(1))

export const mapIloks = R.compose(
  R.split(','),
  R.propOr([], 'value')
)
export const mapNumber = R.compose(
  R.tryCatch(parseInt, R.always(0)),
  R.prop('value')
)
export const getValue = R.cond([
  [R.propEq('name', 'ilok_accounts'), mapIloks], // FIXME: should just be ilok_accounts or ilokAccounts
  [R.propEq('name', 'ilokAccounts'), mapIloks],
  [R.propEq('type', 'checkbox'), R.prop('checked')],
  [R.propEq('type', 'checkboxgroup'), R.prop('value')], // added this here explicitely to distinguish checkbox groups from checkboxes
  // [R.propEq('type', 'number'), mapNumber],
  [R.T, R.prop('value')]
]) // ({ name, type, value, checked })

const basePathOr = (basePath, def, path, state) => R.defaultTo(def, R.pathOr(def, basePath.concat(path), state))
export const getPageData = (basePath, state) => ({
  items: basePathOr(basePath, [], ['listing', 'response', 'results'], state),
  request: basePathOr(basePath, {isLoading: false}, ['listing', 'request'], state),
  total: basePathOr(basePath, 0, ['listing', 'response', 'total'], state),
  pageNum: basePathOr(basePath, 1, ['listing', 'response', 'pageNum'], state),
  perPage: basePathOr(basePath, 20, ['listing', 'response', 'perPage'], state),

  navigation: basePathOr(basePath, {pageNum: 1, perPage: 20}, ['navigation'], state),
  // ac related suff
  autocomplete: {
    selections: basePathOr(basePath, [], ['autocomplete', 'selections'], state),
    suggestions: basePathOr(basePath, [], ['autocomplete', 'response', 'results'], state),
    isLoading: basePathOr(basePath, false, ['autocomplete', 'request', 'isLoading'], state),
    total: basePathOr(basePath, 0, ['autocomplete', 'response', 'total'], state),
    pageNum: basePathOr(basePath, 1, ['autocomplete', 'response', 'pageNum'], state)
  }
})
export const toQueryString = R.compose(
  s => R.isEmpty(s) ? '' : `?${s}`, // Prepend a ?
  R.join('&'), // Join each segment of the query with '&'
  R.map(R.join('=')), // Join the key-value pairs with '='
  R.map(R.map(encodeURIComponent)), // encode keys and values
  R.sortBy(R.prop(0)), // sort it so that its always the same
  R.toPairs // convert the object to pairs like `['limit', 5]`
)
export const isAuthAdmin = R.compose(
  notNilOrEmpty,
  R.find(R.equals(SYSTEM_ROLES.AUTH_ADMIN)),
  R.defaultTo([]),
  R.prop('roles'),
  R.defaultTo({})
)

export const isLicenseAdmin = R.compose(
  notNilOrEmpty,
  R.find(R.equals(SYSTEM_ROLES.LICENSE_ADMIN)),
  R.defaultTo([]),
  R.prop('roles'),
  R.defaultTo({})
)

export const isLicenseViewer = R.compose(
  notNilOrEmpty,
  R.find(R.equals(SYSTEM_ROLES.LICENSE_VIEWER)),
  R.defaultTo([]),
  R.prop('roles'),
  R.defaultTo({})
)

export const isInstAdmin = R.compose(
  notNilOrEmpty,
  R.defaultTo([]),
  R.prop('institutions'),
  R.defaultTo({})
)

export const isRoleAdmin = (user) => {
  const roles = user.roles || [];

  return roles.includes(SYSTEM_ROLES.ROLE_MODERATOR) ||
    roles.includes(SYSTEM_ROLES.AUTH_ADMIN);
};

export const isSystemRoleAdmin = (user) => {
  const roles = user.roles || [];
  return roles.includes(SYSTEM_ROLES.AUTH_ADMIN);
};

export const isMaxVersionAdmin = (user) => {
  const roles = user.roles || [];
  return roles.includes(SYSTEM_ROLES.AUTH_ADMIN) ||
    roles.includes(SYSTEM_ROLES.VERSION_MODERATOR);
};

export const productNameToUpper = R.compose(
  R.join(' '),
  R.map(R.compose(
    R.join(''),
    R.juxt([R.compose(R.toUpper, R.head), R.tail])
  )),
  R.split(' '),
  R.replace(/-/g, ' '),
  R.defaultTo('Unknown product')
)

const isClassic = refId => refId && refId.slice(0, 3) === 'CYC'
const getFsUrlClassic = ref => `https://springboard.fastspring.com/order/search.xml?pRef=&query=${ref}`
const getFsUrlCtxOrder = ref => `https://dashboard.fastspring.com/order/home.xml?mRef=AcquisitionTransaction:${ref}`
const getFsUrlCtxSub = ref => `https://dashboard.fastspring.com/subscription/home.xml?mRef=Subscription:${ref}`
const getFsUrlCtxReturn = ref => `https://dashboard.fastspring.com/order/return/home.xml?mRef=AcquisitionReturnTransaction:${ref}`
const getFsUrlCtx = R.cond([
  [R.equals('order'), t => getFsUrlCtxOrder],
  [R.equals('sub'), t => getFsUrlCtxSub],
  [R.equals('return'), t => getFsUrlCtxReturn],
  [R.T, getFsUrlCtxOrder]
])
export const getFsUrl = type => R.ifElse(
  isClassic,
  getFsUrlClassic,
  getFsUrlCtx(type)
)

export const NEVER = new Date('7474-07-04T04:00:00Z');

export const formatDate = R.cond([
  [isNilOrEmpty, R.always(null)],
  [R.compose(R.equals(NEVER), x => new Date(x)), R.always('never')],
  [R.T, R.tryCatch(x => moment(x).format('YYYY-MM-DD'), R.identity)]
]);

export const capitalizeStr = str => str && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : '';



// WEBPACK FOOTER //
// ./webAdmin/utils/index.js